import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type PagedResponse, type Response } from '@/lib/api'
import { capitalizedText, formatDate } from '@/lib/format'
import { DataNotFound } from '@/components/DataNotFound'
import { Pagination } from '@/components/Pagination'
import { SearchableSelect, type SelectOption } from '@/components/SearchableSelect'
import { formatScore, scoreTextClass, type VacancyRow } from '@/features/vacancy/types'

/**
 * The shared skeleton behind Views/Profile/SavedVacancy.cshtml and
 * ApplicationHistory.cshtml.
 *
 * The two pages, and their two ~520-line scripts, were the same screen: three
 * filter dropdowns scoped to this candidate's own rows, a records-per-page
 * selector, a grid of cards and pagination. Only the card footer differs, so
 * that is a render prop.
 *
 * The filter endpoints differ per page (`listSaved` vs `listApplicationHistory`)
 * and are passed in. None of them take an email any more — the BFF uses the
 * session's.
 */
const RECORD_OPTIONS = [15, 30, 45, 60]

export interface VacancyListPageProps {
  /** POST endpoint returning the paged rows. */
  listUrl: string
  /** GET endpoints for the three filter dropdowns. */
  careerUrl: string
  companyUrl: string
  positionUrl: string
  /** Rendered inside each card's footer. */
  renderFooter: (vacancy: VacancyRow) => ReactNode
  /** Extra lines inside the card body, above the fit-profile score. */
  renderExtraBody?: (vacancy: VacancyRow) => ReactNode
  /** SavedVacancy shows a filled bookmark that unsaves on click. */
  renderHeaderAction?: (vacancy: VacancyRow) => ReactNode
}

interface NamedRow {
  mstCareerID?: number
  CareerName?: string | null
  mstCompanyID?: number
  CompanyName?: string | null
  mstPositionID?: number
  PositionName?: string | null
}

export function VacancyListPage({
  listUrl,
  careerUrl,
  companyUrl,
  positionUrl,
  renderFooter,
  renderExtraBody,
  renderHeaderAction,
}: VacancyListPageProps): JSX.Element {
  const [company, setCompany] = useState('')
  const [position, setPosition] = useState('')
  const [career, setCareer] = useState('')
  const [applied, setApplied] = useState({ company: 0, position: 0, career: 0 })
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(15)

  const careers = useQuery({
    queryKey: ['profile-filter', careerUrl],
    queryFn: () => api.get<Response<NamedRow[]>>(careerUrl),
    staleTime: 5 * 60 * 1000,
  })
  const companies = useQuery({
    queryKey: ['profile-filter', companyUrl],
    queryFn: () => api.get<Response<NamedRow[]>>(companyUrl),
    staleTime: 5 * 60 * 1000,
  })
  const positions = useQuery({
    queryKey: ['profile-filter', positionUrl],
    queryFn: () => api.get<Response<NamedRow[]>>(positionUrl),
    staleTime: 5 * 60 * 1000,
  })

  const listing = useQuery({
    queryKey: ['profile-list', listUrl, applied, pageNumber, pageSize],
    queryFn: () =>
      api.post<PagedResponse<VacancyRow>>(listUrl, {
        mstCompanyID: applied.company,
        mstCareerID: applied.career,
        mstPositionID: applied.position,
        PageNumber: pageNumber,
        PageSize: pageSize,
      }),
  })

  const rows = listing.data?.Data ?? []

  const toOptions = (
    data: NamedRow[] | null | undefined,
    idKey: keyof NamedRow,
    nameKey: keyof NamedRow,
  ): SelectOption[] =>
    (data ?? []).map((item) => ({
      value: String(item[idKey] ?? ''),
      label: String(item[nameKey] ?? ''),
    }))

  return (
    <div className="content">
      <section className="bg-white pt-0">
        <div className="container">
          <div className="row">
            <div className="col-md-4 mb-2 p-1">
              <SearchableSelect
                id="slscompany"
                options={toOptions(companies.data?.Data, 'mstCompanyID', 'CompanyName')}
                value={company}
                onChange={setCompany}
                placeholder="Choose Company"
              />
            </div>
            <div className="col-md-4 mb-2 p-1">
              <SearchableSelect
                id="slsposition"
                options={toOptions(positions.data?.Data, 'mstPositionID', 'PositionName')}
                value={position}
                onChange={setPosition}
                placeholder="Choose Position"
              />
            </div>
            <div className="col-md-3 mb-2 p-1">
              <SearchableSelect
                id="slscareer"
                options={toOptions(careers.data?.Data, 'mstCareerID', 'CareerName')}
                value={career}
                onChange={setCareer}
                placeholder="Choose Career"
              />
            </div>
            <div className="col-md-1 mb-2 p-1 text-center">
              <button
                type="button"
                className="btn btn-primary d-inline-flex"
                id="btnSearch"
                onClick={() => {
                  setPageNumber(1)
                  setApplied({
                    company: company === '' ? 0 : Number(company),
                    position: position === '' ? 0 : Number(position),
                    career: career === '' ? 0 : Number(career),
                  })
                }}
              >
                <i className="ti ti-search me-1" />
                Search
              </button>
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-4 col-md-2 mb-3">
              <select
                className="form-control"
                id="slsrecord"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value))
                  setPageNumber(1)
                }}
              >
                {RECORD_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-7 col-8">
              <h5>Records</h5>
            </div>
          </div>

          {listing.isFetching && (
            <div className="row g-4 mb-5 mt-5" id="loader">
              <div className="d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          )}

          {!listing.isFetching && rows.length === 0 && (
            <div className="row g-4" id="pnlDataNotFound">
              <DataNotFound />
            </div>
          )}

          <div className="row g-4 mb-5 mt-5" id="pnlListVacancy">
            {rows.map((vacancy) => (
              <div
                key={vacancy.trxVacancyID}
                className="col-lg-4 col-md-6 wow animate__fadeInUp"
                data-wow-delay="0.2s"
                style={{
                  visibility: 'visible',
                  animationDelay: '0.2s',
                  animationName: 'fadeInUp',
                }}
              >
                <div className="card">
                  <div className="card-header p-3 position-relative">
                    {/* Both pages hardcoded the TBIG logo here — unlike the
                        main listing, they never branded per company. */}
                    <img className="img-fluid wid-90" src="/lib/images/logo.png" alt="User image" />
                    {renderHeaderAction?.(vacancy)}
                  </div>

                  <div className="card-body">
                    <h5 className="card-title mb-3">{vacancy.PositionName}</h5>
                    <h6 className="card-subtitle mb-3 text-muted">{vacancy.CompanyName}</h6>
                    <ul className="list-inline my-3 card-text text-muted text-sm">
                      <li className="list-inline-item">
                        <i className="ti ti-user text-primary" />
                        <span id="spnCareer" className="m-l-5">
                          {vacancy.CareerName}
                        </span>
                      </li>
                      <li className="list-inline-item">
                        <i className="ti ti-id text-primary" />
                        <span id="spnJobContracts" className="m-l-5">
                          {vacancy.JobContractsName}
                        </span>
                      </li>
                      <li className="list-inline-item">
                        <i className="ti ti-map-pin text-primary" />
                        <span id="spnLocation" className="m-l-5">
                          {capitalizedText((vacancy.ProvinceName ?? '').toLowerCase())}
                        </span>
                      </li>
                    </ul>

                    <p className="card-text text-muted text-sm">
                      <i className="ti ti-calendar-event text-primary" /> Deadline :{' '}
                      {formatDate(vacancy.EndVacancy)}
                    </p>

                    {renderExtraBody?.(vacancy)}

                    {vacancy.Score !== null && vacancy.Score !== undefined && (
                      <p className="card-text text-muted text-sm fw-bold">
                        <i className="ti ti-target text-primary" /> Vacancy fit profile:{' '}
                        <span className={scoreTextClass(vacancy.Score)}>
                          {formatScore(vacancy.Score)}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="card-footer p-2 d-flex flex-wrap justify-content-center gap-2">
                    {renderFooter(vacancy)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-4">
            <Pagination
              count={listing.data?.Count ?? 0}
              pageNumber={pageNumber}
              pageSize={pageSize}
              onPageChange={setPageNumber}
            />
          </div>
        </div>
      </section>
    </div>
  )
}

/** Shared by both pages' footers. */
export function ShowMoreLink({ trxVacancyID }: { trxVacancyID: number }): JSX.Element {
  return (
    <Link to={`/Vacancy/InfoRedirect/${trxVacancyID}`} className="btn btn-outline-primary" id="btnShowMore">
      Show More
    </Link>
  )
}
